import { CourierDto, KnownUserStatus } from '@groundfloor-platform/mgmt-service-g-sdk';
import { AddRegistrationPage } from '../../../../ui/components/pure/icons/AddRegistrationPage.icon';
import { Approval } from '../../../../ui/components/pure/icons/Approval.icon';
import { Edit } from '../../../../ui/components/pure/icons/Edit.icon';
import { Enable } from '../../../../ui/components/pure/icons/Enable.icon';
import { Suspend } from '../../../../ui/components/pure/icons/Suspend.icon';
import { Switch } from '../../../../ui/components/pure/icons/Switch.icon';
import { ActionsButtonItem } from '../../../../ui/components/pure/actions-button/actions-button.types';
import { makeDefineActions } from '../../../../ui/components/pure/actions-button/actions-button.helpers';
import { Permissions } from '../../../../core/permissions/permissions.typings';
import { isCourierIncomplete, isCourierRejected } from '../../logic/courier-form.map';

export enum CourierDetailsActions {
  COMPLETE_REGISTRATION = 'COMPLETE_REGISTRATION',
  COMPLETE_REGISTRATION_SUCCESS = 'COMPLETE_REGISTRATION_SUCCESS',
  EDIT_COURIER = 'EDIT_COURIER',
  EDIT_COURIER_SUCCESS = 'EDIT_COURIER_SUCCESS',
  EDIT_APPROVAL = 'EDIT_APPROVAL',
  EDIT_APPROVAL_APPROVE = 'EDIT_APPROVAL_APPROVE',
  EDIT_APPROVAL_REJECT = 'EDIT_APPROVAL_REJECT',
  EDIT_APPROVAL_SUCCESS = 'EDIT_APPROVAL_SUCCESS',
  MOVE_COURIER = 'MOVE_COURIER',
  MOVE_COURIER_SUCCESS = 'MOVE_COURIER_SUCCESS',
  ACTIVATE_COURIER = 'ACTIVATE_COURIER',
  DEACTIVATE_COURIER = 'DEACTIVATE_COURIER',
  STATUS_CHANGE_SUCCESS = 'STATUS_CHANGE_SUCCESS',
  STATUS_CHANGE_ERROR = 'STATUS_CHANGE_ERROR',
  ADD_NOTE = 'ADD_NOTE',
  ADD_NOTE_SUCCESS = 'ADD_NOTE_SUCCESS'
}

/**
 * A courier's actions are their own rather than the generic user ones: capabilities are inherited
 * from the carrier and not editable here, and approving a self-registration has its own rules.
 */
function makeCourierDetailsActions(projectID: string): ActionsButtonItem<CourierDetailsActions>[] {
  return [
    {
      title: 'Complete registration',
      Icon: AddRegistrationPage,
      action: CourierDetailsActions.COMPLETE_REGISTRATION,
      availableUiFeatures: ['internal-groundfloor'],
      permission: {
        action: Permissions.users.update,
        projectID
      }
    },
    {
      title: 'Edit courier',
      Icon: Edit,
      action: CourierDetailsActions.EDIT_COURIER,
      permission: {
        action: Permissions.users.update,
        projectID
      }
    },
    {
      title: 'Edit approval',
      Icon: Approval,
      action: CourierDetailsActions.EDIT_APPROVAL,
      permission: {
        action: Permissions.users.update,
        projectID
      }
    },
    {
      title: 'Move courier',
      Icon: Switch,
      action: CourierDetailsActions.MOVE_COURIER,
      permission: {
        action: Permissions.groups.moveUser,
        projectID
      }
    },
    {
      dividerBefore: true,
      title: 'Reactivate',
      Icon: Enable,
      action: CourierDetailsActions.ACTIVATE_COURIER,
      permission: {
        action: Permissions.users.update,
        projectID
      }
    },
    {
      dividerBefore: true,
      title: 'Deactivate',
      Icon: Suspend,
      action: CourierDetailsActions.DEACTIVATE_COURIER,
      permission: {
        action: Permissions.users.update,
        projectID
      }
    }
  ];
}

/**
 * Only an active courier can be deactivated or moved, and only an inactive one reactivated — the
 * same rule the other user types follow. Edit and Edit approval are offered whatever state they
 * are in — a pending courier is who an approval is for.
 */
function makeValidateAction(courier: Pick<CourierDto, 'status' | 'accessLevel'>) {
  return function validateAction(action: CourierDetailsActions) {
    // The courier endpoints refuse everything else for an incomplete courier.
    if (isCourierIncomplete(courier)) {
      switch (action) {
        case CourierDetailsActions.COMPLETE_REGISTRATION:
          return courier.status !== KnownUserStatus.Deleted;
        case CourierDetailsActions.DEACTIVATE_COURIER:
          return courier.status === KnownUserStatus.Incomplete;
        default:
          return false;
      }
    }

    switch (action) {
      case CourierDetailsActions.COMPLETE_REGISTRATION:
        return false;
      case CourierDetailsActions.DEACTIVATE_COURIER:
        return courier.status === KnownUserStatus.Active;
      // Reactivating a rejected courier would clear the suspension but leave the rejection
      // standing. Approving them is the way back, and sets both.
      case CourierDetailsActions.ACTIVATE_COURIER:
        return courier.status === KnownUserStatus.Inactive && !isCourierRejected(courier);
      // Active only: an inactive courier is reactivated first and moved as a separate step.
      case CourierDetailsActions.MOVE_COURIER:
        return courier.status === KnownUserStatus.Active;
      // The endpoint refuses a deleted courier until they are restored.
      case CourierDetailsActions.EDIT_APPROVAL:
        return courier.status !== KnownUserStatus.Deleted;
      default:
        return true;
    }
  };
}

/**
 * Authorised against the project the page was reached through, which the viewer holds. Not the
 * courier's `project`: that is the company above a region's admins, and ActionsButton drops the
 * whole menu when no item passes. The API authorises the edit against the courier itself.
 */
export function makeDefineCourierDetailsActions(courier: CourierDto, projectID: string) {
  return makeDefineActions(makeCourierDetailsActions(projectID), undefined, makeValidateAction(courier));
}
